// src/components/ElevesManagement.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { collection, query, onSnapshot, addDoc, updateDoc, doc, deleteDoc, where, orderBy, getDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { PlusCircle, Edit, Trash2, User, Users, Loader2, Search, Printer, CheckCircle, UserPlus, ClipboardList, ClipboardCheck, FileUp, Hash, Save, FileText } from 'lucide-react';

// 🔥 DÉFINITION DE LA FONCTION UTILITAIRE AU NIVEAU DU MODULE
const formatDate = (dateValue) => {
    let dateObject;
    if (!dateValue) return 'N/A'; 

    // Gère l'objet Firestore Timestamp (si la méthode .toDate() existe)
    if (dateValue.toDate) {
        dateObject = dateValue.toDate();
    } 
    // Gère les chaînes ou les objets Date natifs
    else if (typeof dateValue === 'string' || dateValue instanceof Date) {
        dateObject = new Date(dateValue);
    } else {
        return 'Format Inconnu';
    }

    if (isNaN(dateObject.getTime())) {
        return 'Date Invalide';
    }

    return dateObject.toLocaleDateString('fr-FR');
};

const ElevesManagement = ({ setMessage, setError, db, schoolId, userRole, onSwitchToExcelImport, appVersion = 'v1' }) => {
  // État pour les données de l'élève (enregistrement initial)
  const [students, setStudents] = useState([]);
  const [newStudentName, setNewStudentName] = useState('');
  const [newStudentDateOfBirth, setNewStudentDateOfBirth] = useState('');
  const [newStudentPlaceOfBirth, setNewStudentPlaceOfBirth] = useState('');
  const [newStudentAddress, setNewStudentAddress] = useState('');
  const [newStudentGender, setNewStudentGender] = useState('');
  const [newStudentGuardianPhone, setNewStudentGuardianPhone] = useState('');
  const [newStudentGuardianFullName, setNewStudentGuardianFullName] = useState('');
  const [newStudentFatherName, setNewStudentFatherName] = useState('');
  const [newStudentMotherName, setNewStudentMotherName] = useState('');
  const [newStudentPhotoUrl, setNewStudentPhotoUrl] = useState(''); // URL enregistrée dans Firestore
  const [newStudentPhotoFile, setNewStudentPhotoFile] = useState(null); // Fichier à télécharger
  const [photoPreview, setPhotoPreview] = useState(null); // Aperçu de la photo pour l'utilisateur

  // États liés à l'interface générale
  const [activeTab, setActiveTab] = useState('newStudent');
  const [isEditing, setIsEditing] = useState(false);
  const [currentStudent, setCurrentStudent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [enrollmentFilterSchoolYear, setEnrollmentFilterSchoolYear] = useState('');
  const [enrollmentSearchTerm, setEnrollmentSearchTerm] = useState('');
  const [enrollmentFilterClassId, setEnrollmentFilterClassId] = useState('');

  const [studentsLoaded, setStudentsLoaded] = useState(false);
  
  const [enrollments, setEnrollments] = useState([]);
  // États pour la gestion de l'inscription
  const [newEnrollmentStudentId, setNewEnrollmentStudentId] = useState('');
  const [newEnrollmentStudentIds, setNewEnrollmentStudentIds] = useState([]); // Pour l'inscription multiple
  const [newEnrollmentClassName, setNewEnrollmentClassName] = useState('');
  const [newEnrollmentClassId, setNewEnrollmentClassId] = useState('');
  const [newEnrollmentDate, setNewEnrollmentDate] = useState(new Date().toISOString().split('T')[0]);
  const [newEnrollmentType, setNewEnrollmentType] = useState('normal');
  const [newEnrollmentBorsierResponsibleName, setNewEnrollmentBorsierResponsibleName] = useState('');
  const [newEnrollmentSchoolYear, setNewEnrollmentSchoolYear] = useState('');
  const [enrollmentMode, setEnrollmentMode] = useState('single');
  const [studentSearchTerm, setStudentSearchTerm] = useState('');
  const [newEnrollmentStudentAuthUID, setNewEnrollmentStudentAuthUID] = useState('');
  // Fonction pour vérifier si la fonctionnalité photo est disponible
const isPhotoFeatureAvailable = useCallback(() => {
  // La photo est disponible dans v2 et v3, pas dans v1
  return appVersion === 'v2' || appVersion === 'v3';
}, [appVersion]);
  // --- NOUVEAUX ÉTATS CAS SOCIAL ---
  const [isCasSocial, setIsCasSocial] = useState(false);
  const [casSocialFeeType, setCasSocialFeeType] = useState('annual');
  const [casSocialFeeValue, setCasSocialFeeValue] = useState('');
  
  const [isEnrolling, setIsEnrolling] = useState(false);
  const [classes, setClasses] = useState([]);
  const [isEditingEnrollment, setIsEditingEnrollment] = useState(false);
  const [enrollmentToEdit, setEnrollmentToEdit] = useState(null);
  
  // Permissions basées sur le rôle
  const canManageStudents = ['superAdmin', 'admin', 'secretaireGeneral'].includes(userRole);
  const canManageEnrollments = ['superAdmin', 'admin', 'secretaireGeneral'].includes(userRole);
  const canAccessExcelImport = ['superAdmin', 'admin'].includes(userRole);

  // Fonction pour déterminer l'année scolaire actuelle
  const getCurrentSchoolYear = useCallback(() => {
    const today = new Date();
    const currentMonth = today.getMonth() + 1;
    let year = today.getFullYear();
    
    if (currentMonth >= 9) {
      return `${year}-${year + 1}`;
    } else {
      return `${year - 1}-${year}`;
    }
  }, []);

  // Fonction pour générer les années scolaires pour la liste déroulante
  const generateSchoolYears = useCallback(() => {
    const years = [];
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;

    let startYearForCurrentSchoolYear;
    if (currentMonth >= 9) {
      startYearForCurrentSchoolYear = currentYear;
    } else {
      startYearForCurrentSchoolYear = currentYear - 1;
    }

    for (let i = 0; i < 4; i++) {
      const year = startYearForCurrentSchoolYear + i;
      years.push(`${year}-${year + 1}`);
    }
    return years;
  }, []);

  const schoolYearOptions = useMemo(() => generateSchoolYears(), [generateSchoolYears]);

  // Set the default school year when the component mounts
  useEffect(() => {
    setNewEnrollmentSchoolYear(getCurrentSchoolYear());
  }, [getCurrentSchoolYear]);

  // Effet pour charger les élèves (optimisé - un seul listener)
  useEffect(() => {
    if (!schoolId) {
      setIsLoading(false);
      return;
    }

    const q = query(collection(db, 'schools', schoolId, 'eleves'), orderBy('name'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const studentsData = snapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data(),
        // Normaliser les dates pour éviter les appels répétés à toDate()
        dateOfBirth: doc.data().dateOfBirth?.toDate ? doc.data().dateOfBirth.toDate() : doc.data().dateOfBirth,
        createdAt: doc.data().createdAt?.toDate ? doc.data().createdAt.toDate() : doc.data().createdAt,
        updatedAt: doc.data().updatedAt?.toDate ? doc.data().updatedAt.toDate() : doc.data().updatedAt
      }));
      setStudents(studentsData);
      setStudentsLoaded(true);
      setIsLoading(false);
    }, (err) => {
      console.error("Error fetching students:", err);
      setError("Erreur lors du chargement des élèves.");
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [db, schoolId, setError]);

  // Effet pour charger les classes (optimisé - un seul listener)
  useEffect(() => {
    if (schoolId) {
      const q = query(collection(db, 'schools', schoolId, 'classes'), orderBy('nom'));
      const unsubscribe = onSnapshot(q, (snapshot) => {
        const classesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setClasses(classesData);
      }, (err) => {
        console.error("Error fetching classes:", err);
        setError("Erreur lors du chargement des classes.");
      });
      return () => unsubscribe();
    }
  }, [db, schoolId, setError]);

  // Effet pour charger les inscriptions (optimisé - un seul listener)
  useEffect(() => {
    if (!schoolId) return;

    const q = query(collection(db, 'schools', schoolId, 'enrollments'), orderBy('enrollmentDate', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const enrollmentsData = snapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data(),
        // Normaliser les dates une seule fois
        enrollmentDate: doc.data().enrollmentDate?.toDate ? doc.data().enrollmentDate.toDate() : doc.data().enrollmentDate,
        createdAt: doc.data().createdAt?.toDate ? doc.data().createdAt.toDate() : doc.data().createdAt,
        updatedAt: doc.data().updatedAt?.toDate ? doc.data().updatedAt.toDate() : doc.data().updatedAt
      }));
      setEnrollments(enrollmentsData);
    }, (err) => {
      console.error("Error fetching enrollments:", err);
      setError("Erreur lors du chargement des inscriptions.");
    });

    return () => unsubscribe();
  }, [db, schoolId, setError]);

  // Mémoization des filtres pour éviter les recalculs inutiles
  const filteredStudents = useMemo(() => {
    return students.filter(student =>
      student.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [students, searchTerm]);

  const filteredStudentsForEnrollment = useMemo(() => {
    if (!studentSearchTerm) return students;
    
    return students.filter(student =>
      student.name.toLowerCase().includes(studentSearchTerm.toLowerCase())
    );
  }, [students, studentSearchTerm]);

  const filteredEnrollments = useMemo(() => {
    let list = enrollments;

    if (enrollmentSearchTerm) {
      const q = enrollmentSearchTerm.toLowerCase();
      list = list.filter(enr =>
        enr.studentName?.toLowerCase().includes(q)
      );
    }

    if (enrollmentFilterSchoolYear) {
      list = list.filter(enr =>
        enr.schoolYear === enrollmentFilterSchoolYear
      );
    }

    if (enrollmentFilterClassId) {
      list = list.filter(enr =>
        enr.classId === enrollmentFilterClassId
      );
    }

    return list;
  }, [enrollments, enrollmentSearchTerm, enrollmentFilterSchoolYear, enrollmentFilterClassId]);

  // Fonction pour télécharger la photo sur Firebase Storage
  const uploadPhotoToFirebase = useCallback(async (file, studentId) => {
    if (!file || !schoolId) return null;

    const storage = getStorage();
    // Utilise l'ID de l'élève pour éviter les doublons
    const fileName = `${studentId}-${Date.now()}.${file.name.split('.').pop()}`;
    const storageRef = ref(storage, `schools/${schoolId}/eleves_photos/${fileName}`);

    setMessage({ type: 'info', text: 'Téléchargement de la photo en cours...' });

    try {
        const snapshot = await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(snapshot.ref);
        setMessage({ type: 'success', text: 'Photo téléchargée avec succès.' });
        return downloadURL;
    } catch (error) {
        console.error("Erreur lors du téléchargement de la photo:", error);
        setError("Erreur lors du téléchargement de la photo : " + error.message);
        throw new Error("Échec du téléchargement de la photo.");
    }
  }, [schoolId, setMessage, setError]);

  const handleCreateStudent = async (e) => {
  e.preventDefault();
  if (!canManageStudents) {
    setError("Vous n'avez pas les autorisations pour créer un élève.");
    return;
  }
  
  if (!schoolId) {
    setError("ID de l'école non disponible. Impossible de créer l'élève.");
    return;
  }

  if (!newStudentName || !newStudentDateOfBirth || !newStudentGender) {
    setError("Veuillez remplir tous les champs obligatoires (Nom, Date de naissance, Sexe).");
    return;
  }

  try {
    setIsLoading(true);
    
    // Créer d'abord le document pour avoir un ID
    const newStudentRef = doc(collection(db, 'schools', schoolId, 'eleves'));
    const studentId = newStudentRef.id;
    
    let photoUrlToSave = '';
    // Upload photo seulement si la fonctionnalité est disponible ET si un fichier est fourni
    if (isPhotoFeatureAvailable() && newStudentPhotoFile) {
      photoUrlToSave = await uploadPhotoToFirebase(newStudentPhotoFile, studentId);
    }

    const studentData = {
      id: studentId,
      name: newStudentName,
      dateOfBirth: new Date(newStudentDateOfBirth),
      placeOfBirth: newStudentPlaceOfBirth,
      address: newStudentAddress,
      gender: newStudentGender,
      guardianPhone: newStudentGuardianPhone,
      guardianFullName: newStudentGuardianFullName,
      fatherName: newStudentFatherName,
      motherName: newStudentMotherName,
      photoUrl: photoUrlToSave,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    // Utiliser setDoc avec l'ID généré
    await updateDoc(newStudentRef, studentData);
    
    setMessage({ type: 'success', text: 'Élève ajouté avec succès !' });
    
    // Réinitialiser le formulaire
    resetStudentForm();
    
  } catch (e) {
    console.error("Error adding student: ", e);
    setError("Erreur lors de l'ajout de l'élève : " + e.message);
  } finally {
    setIsLoading(false);
  }
};

  // Fonction pour réinitialiser le formulaire élève
  const resetStudentForm = useCallback(() => {
    setNewStudentName('');
    setNewStudentDateOfBirth('');
    setNewStudentPlaceOfBirth('');
    setNewStudentAddress('');
    setNewStudentGender('');
    setNewStudentGuardianPhone('');
    setNewStudentGuardianFullName('');
    setNewStudentFatherName('');
    setNewStudentMotherName('');
    setNewStudentPhotoUrl('');
    setNewStudentPhotoFile(null);
    setPhotoPreview(null);
    setIsEditing(false);
    setCurrentStudent(null);
  }, []);

  const handleEditStudent = useCallback((student) => {
    if (!canManageStudents) {
      setError("Vous n'avez pas les autorisations pour modifier un élève.");
      return;
    }
    
    setIsEditing(true);
    setCurrentStudent(student);
    setNewStudentName(student.name);
    setNewStudentDateOfBirth(student.dateOfBirth ? 
      (student.dateOfBirth instanceof Date ? 
        student.dateOfBirth.toISOString().split('T')[0] : 
        new Date(student.dateOfBirth).toISOString().split('T')[0]) : 
      '');
    setNewStudentPlaceOfBirth(student.placeOfBirth);
    setNewStudentAddress(student.address);
    setNewStudentGender(student.gender);
    setNewStudentGuardianPhone(student.guardianPhone);
    setNewStudentGuardianFullName(student.guardianFullName);
    setNewStudentFatherName(student.fatherName);
    setNewStudentMotherName(student.motherName);
    setNewStudentPhotoUrl(student.photoUrl || '');
    setNewStudentPhotoFile(null);
    setPhotoPreview(student.photoUrl || null);
    setActiveTab('newStudent');
  }, [canManageStudents, setError]);

  const handleUpdateStudent = async (e) => {
  e.preventDefault();
  if (!canManageStudents) {
    setError("Vous n'avez pas les autorisations pour mettre à jour un élève.");
    return;
  }
  
  if (!schoolId || !currentStudent?.id) {
    setError("ID de l'école ou de l'élève non disponible. Impossible de mettre à jour.");
    return;
  }

  try {
    setIsLoading(true);
    
    let photoUrlToSave = newStudentPhotoUrl;

    // Upload photo seulement si la fonctionnalité est disponible ET si un nouveau fichier est fourni
    if (isPhotoFeatureAvailable() && newStudentPhotoFile) {
      photoUrlToSave = await uploadPhotoToFirebase(newStudentPhotoFile, currentStudent.id);
    }

    const studentDocRef = doc(db, 'schools', schoolId, 'eleves', currentStudent.id);
    await updateDoc(studentDocRef, {
      name: newStudentName,
      dateOfBirth: new Date(newStudentDateOfBirth),
      placeOfBirth: newStudentPlaceOfBirth,
      address: newStudentAddress,
      gender: newStudentGender,
      guardianPhone: newStudentGuardianPhone,
      guardianFullName: newStudentGuardianFullName,
      fatherName: newStudentFatherName,
      motherName: newStudentMotherName,
      photoUrl: photoUrlToSave,
      updatedAt: new Date(),
    });
    
    setMessage({ type: 'success', text: 'Informations de l\'élève mises à jour avec succès !' });
    resetStudentForm();
    setActiveTab('studentList');
    
  } catch (e) {
    console.error("Error updating student: ", e);
    setError("Erreur lors de la mise à jour de l'élève : " + e.message);
  } finally {
    setIsLoading(false);
  }
};

  const handleDeleteStudent = async (id) => {
    if (!canManageStudents) {
      setError("Vous n'avez pas les autorisations pour supprimer un élève.");
      return;
    }
    
    if (!schoolId) {
      setError("ID de l'école non disponible. Impossible de supprimer l'élève.");
      return;
    }

    if (window.confirm("Êtes-vous sûr de vouloir supprimer cet élève ? Cette action supprimera également toutes ses inscriptions.")) {
      try {
        setIsLoading(true);
        
        // Supprimer également les inscriptions de cet élève
        const enrollmentsToDelete = enrollments.filter(e => e.studentId === id);
        for (const enrollment of enrollmentsToDelete) {
          await deleteDoc(doc(db, 'schools', schoolId, 'enrollments', enrollment.id));
        }
        
        await deleteDoc(doc(db, 'schools', schoolId, 'eleves', id));
        setMessage({ type: 'success', text: 'Élève et ses inscriptions supprimés avec succès !' });
        
      } catch (e) {
        console.error("Error deleting student: ", e);
        setError("Erreur lors de la suppression de l'élève : " + e.message);
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Fonction pour gérer la création d'inscription
  const handleCreateEnrollment = async (e) => {
    e.preventDefault();
    if (!canManageEnrollments) {
      setError("Vous n'avez pas les autorisations pour gérer les inscriptions.");
      return;
    }
    
    if (!schoolId) {
      setError("ID de l'école non disponible. Impossible de créer l'inscription.");
      return;
    }

    // Vérification selon le mode
    if (enrollmentMode === 'single') {
      if (!newEnrollmentStudentId || !newEnrollmentClassId || !newEnrollmentDate || !newEnrollmentSchoolYear || !newEnrollmentType) {
        setError("Veuillez remplir tous les champs obligatoires (Élève, Classe, Date, Année Scolaire, Type d'Inscription).");
        return;
      }
    } else if (enrollmentMode === 'multiple') {
      if (newEnrollmentStudentIds.length === 0 || !newEnrollmentClassId || !newEnrollmentDate || !newEnrollmentSchoolYear || !newEnrollmentType) {
        setError("Veuillez sélectionner au moins un élève et remplir tous les champs obligatoires (Classe, Date, Année Scolaire, Type d'Inscription).");
        return;
      }
    }

    // Validation cas social
    if (isCasSocial) {
      const feeValue = parseFloat(casSocialFeeValue);
      if (isNaN(feeValue) || feeValue <= 0) {
        setError("Veuillez spécifier une valeur numérique positive pour le cas social (montant ou pourcentage).");
        setIsEnrolling(false);
        return;
      }
      if (casSocialFeeType === 'percentage' && (feeValue < 1 || feeValue > 100)) {
        setError("Le pourcentage de réduction doit être compris entre 1 et 100.");
        setIsEnrolling(false);
        return;
      }
    }

    try {
      setIsLoading(true);
      setIsEnrolling(true);

      const studentsToEnroll = enrollmentMode === 'single' 
        ? [{ id: newEnrollmentStudentId }]
        : newEnrollmentStudentIds.map(id => ({ id }));

      const enrollmentPromises = studentsToEnroll.map(async (student) => {
        const selectedStudent = students.find(s => s.id === student.id);
        const studentNameToSave = selectedStudent ? selectedStudent.name : 'Élève inconnu';

        return addDoc(collection(db, 'schools', schoolId, 'enrollments'), {
          studentId: student.id,
          studentName: studentNameToSave,
          studentAuthUID: newEnrollmentStudentAuthUID,
          className: newEnrollmentClassName,
          classId: newEnrollmentClassId,
          enrollmentDate: new Date(newEnrollmentDate),
          schoolYear: newEnrollmentSchoolYear,
          enrollmentType: newEnrollmentType,
          borsierResponsibleName: (newEnrollmentType === 'boursier' || newEnrollmentType === 'demi-boursier' || newEnrollmentType === 'social')
            ? newEnrollmentBorsierResponsibleName
            : '',
          isCasSocial: isCasSocial,
          casSocialFeeType: isCasSocial ? casSocialFeeType : null,
          casSocialFeeValue: isCasSocial ? parseFloat(casSocialFeeValue) : 0,
          schoolId: schoolId,
          createdAt: new Date(),
        });
      });

      await Promise.all(enrollmentPromises);

      setMessage({ 
        type: 'success', 
        text: `${studentsToEnroll.length} élève(s) inscrit(s) avec succès !` 
      });

      resetEnrollmentForm();

    } catch (e) {
      console.error("Error adding enrollment: ", e);
      setError("Erreur lors de l'ajout de l'inscription : " + e.message);
    } finally {
      setIsLoading(false);
      setIsEnrolling(false);
    }
  };

  // Fonction pour réinitialiser le formulaire d'inscription
  const resetEnrollmentForm = useCallback(() => {
    setNewEnrollmentStudentId('');
    setNewEnrollmentStudentIds([]);
    setNewEnrollmentClassId('');
    setNewEnrollmentClassName('');
    setNewEnrollmentDate(new Date().toISOString().split('T')[0]);
    setNewEnrollmentSchoolYear(getCurrentSchoolYear());
    setNewEnrollmentType('normal');
    setNewEnrollmentBorsierResponsibleName('');
    setEnrollmentMode('single');
    setStudentSearchTerm('');
    setIsCasSocial(false);
    setCasSocialFeeType('annual');
    setCasSocialFeeValue('');
    setIsEditingEnrollment(false);
    setEnrollmentToEdit(null);
  }, [getCurrentSchoolYear]);

  const handleUpdateEnrollment = async (e) => {
    e.preventDefault();

    if (!canManageEnrollments) {
      setError("Vous n'avez pas les autorisations pour modifier une inscription.");
      return;
    }
    
    if (!schoolId || !enrollmentToEdit?.id) {
      setError("ID de l'école ou de l'inscription non disponible. Impossible de mettre à jour.");
      return;
    }

    if (!newEnrollmentStudentId || !newEnrollmentClassId || !newEnrollmentDate || !newEnrollmentSchoolYear || !newEnrollmentType) {
      setError("Veuillez remplir tous les champs obligatoires (Élève, Classe, Date, Année Scolaire, Type d'Inscription).");
      return;
    }

    // Validation cas social
    if (isCasSocial) {
      const feeValue = parseFloat(casSocialFeeValue);
      if (isNaN(feeValue) || feeValue <= 0) {
        setError("Veuillez spécifier une valeur numérique positive pour le cas social (montant ou pourcentage).");
        setIsEnrolling(false);
        return;
      }
      if (casSocialFeeType === 'percentage' && (feeValue < 1 || feeValue > 100)) {
        setError("Le pourcentage de réduction doit être compris entre 1 et 100.");
        setIsEnrolling(false);
        return;
      }
    }

    try {
      setIsLoading(true);
      setIsEnrolling(true);

      const selectedStudent = students.find(s => s.id === newEnrollmentStudentId);
      const studentNameToSave = selectedStudent ? selectedStudent.name : 'Élève inconnu';

      const enrollmentDocRef = doc(db, 'schools', schoolId, 'enrollments', enrollmentToEdit.id);
      await updateDoc(enrollmentDocRef, {
        studentId: newEnrollmentStudentId,
        studentName: studentNameToSave,
        studentAuthUID: newEnrollmentStudentAuthUID,
        className: newEnrollmentClassName,
        enrollmentDate: new Date(newEnrollmentDate),
        schoolYear: newEnrollmentSchoolYear,
        enrollmentType: newEnrollmentType,
        borsierResponsibleName: (newEnrollmentType === 'boursier' || newEnrollmentType === 'demi-boursier' || newEnrollmentType === 'social')
          ? newEnrollmentBorsierResponsibleName
          : '',
        isCasSocial: isCasSocial,
        casSocialFeeType: isCasSocial ? casSocialFeeType : null,
        casSocialFeeValue: isCasSocial ? parseFloat(casSocialFeeValue) : 0,
        updatedAt: new Date(),
      });

      setMessage({ type: 'success', text: 'Inscription mise à jour avec succès !' });
      resetEnrollmentForm();
      setActiveTab('enrolledList');
      
    } catch (e) {
      console.error("Error updating enrollment: ", e);
      setError("Erreur lors de la mise à jour de l'inscription : " + e.message);
    } finally {
      setIsLoading(false);
      setIsEnrolling(false);
    }
  };

  const handleEditEnrollment = useCallback((enrollment) => {
    setNewEnrollmentStudentId(enrollment.studentId);
    setNewEnrollmentClassId(enrollment.classId);
    setNewEnrollmentClassName(enrollment.className);
    setNewEnrollmentSchoolYear(enrollment.schoolYear);
    setNewEnrollmentStudentAuthUID(enrollment.studentAuthUID || '');
    setNewEnrollmentDate(enrollment.enrollmentDate ? 
      (enrollment.enrollmentDate instanceof Date ? 
        enrollment.enrollmentDate.toISOString().split('T')[0] : 
        new Date(enrollment.enrollmentDate).toISOString().split('T')[0]) : 
      new Date().toISOString().split('T')[0]);
    setNewEnrollmentType(enrollment.enrollmentType);
    setNewEnrollmentBorsierResponsibleName(enrollment.borsierResponsibleName || '');
    setEnrollmentMode('single');
    
    // Préremplissage cas social
    setIsCasSocial(enrollment.isCasSocial || false);
    setCasSocialFeeType(enrollment.casSocialFeeType || 'annual');
    setCasSocialFeeValue(enrollment.casSocialFeeValue?.toString() || '');

    setIsEditingEnrollment(true);
    setEnrollmentToEdit(enrollment);
    setActiveTab('enrollment');
  }, []);

  const handleSelectAllVisibleStudents = useCallback(() => {
    const visibleStudentIds = filteredStudentsForEnrollment.map(student => student.id);
    const allVisibleSelected = visibleStudentIds.every(id => 
      newEnrollmentStudentIds.includes(id)
    );
    
    if (allVisibleSelected) {
      setNewEnrollmentStudentIds(prev => 
        prev.filter(id => !visibleStudentIds.includes(id))
      );
    } else {
      const newSelectedIds = [...new Set([...newEnrollmentStudentIds, ...visibleStudentIds])];
      setNewEnrollmentStudentIds(newSelectedIds);
    }
  }, [filteredStudentsForEnrollment, newEnrollmentStudentIds]);

  // Fonction pour imprimer la fiche technique
  const handlePrintStudentProfile = useCallback((student) => {
    if (!student || !schoolId) {
      setError("Informations manquantes pour générer la fiche.");
      return;
    }

    try {
      const studentEnrollments = enrollments.filter(e => e.studentId === student.id);
      const recentEnrollment = studentEnrollments[0];
      const className = recentEnrollment ? recentEnrollment.className : 'Non inscrit';

      const printContent = `
        <html>
          <head>
            <title>Fiche Technique - ${student.name}</title>
            <style>
              body { font-family: 'Arial', sans-serif; padding: 20px; line-height: 1.6; }
              .container { max-width: 800px; margin: 0 auto; }
              .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 20px; }
              .header h1 { margin: 0; color: #1e40af; font-size: 24pt; }
              .info-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; margin-bottom: 30px; }
              .info-section { margin-bottom: 20px; }
              .info-section h3 { background-color: #f0f0f0; padding: 10px; border-left: 4px solid #1e40af; }
              .info-row { display: flex; margin-bottom: 8px; }
              .info-label { font-weight: bold; min-width: 200px; }
              .info-value { flex: 1; }
              .photo-container { text-align: center; margin: 20px 0; }
              .photo-container img { max-width: 150px; border: 2px solid #ccc; border-radius: 5px; }
              .footer { margin-top: 50px; padding-top: 20px; border-top: 1px solid #ccc; text-align: center; font-size: 12px; color: #666; }
              @media print {
                body { margin: 0; padding: 0; }
                .container { box-shadow: none; }
              }
            </style>
          </head>
          <body>
            <div class="container">
              <div class="header">
                <h1>FICHE TECHNIQUE DE L'ÉLÈVE</h1>
                <p>Date de génération: ${new Date().toLocaleDateString('fr-FR')}</p>
              </div>

              ${student.photoUrl ? `
                <div class="photo-container">
                  <img src="${student.photoUrl}" alt="Photo de ${student.name}" onerror="this.style.display='none'"/>
                </div>
              ` : ''}

              <div class="info-section">
                <h3>INFORMATIONS PERSONNELLES</h3>
                <div class="info-grid">
                  <div class="info-row">
                    <div class="info-label">Nom complet:</div>
                    <div class="info-value">${student.name}</div>
                  </div>
                  <div class="info-row">
                    <div class="info-label">Date de naissance:</div>
                    <div class="info-value">${formatDate(student.dateOfBirth)}</div>
                  </div>
                  <div class="info-row">
                    <div class="info-label">Lieu de naissance:</div>
                    <div class="info-value">${student.placeOfBirth || 'Non renseigné'}</div>
                  </div>
                  <div class="info-row">
                    <div class="info-label">Sexe:</div>
                    <div class="info-value">${student.gender}</div>
                  </div>
                  <div class="info-row">
                    <div class="info-label">Adresse:</div>
                    <div class="info-value">${student.address || 'Non renseignée'}</div>
                  </div>
                  <div class="info-row">
                    <div class="info-label">Classe actuelle:</div>
                    <div class="info-value">${className}</div>
                  </div>
                </div>
              </div>

              <div class="info-section">
                <h3>INFORMATIONS FAMILIALES</h3>
                <div class="info-grid">
                  <div class="info-row">
                    <div class="info-label">Nom du père:</div>
                    <div class="info-value">${student.fatherName || 'Non renseigné'}</div>
                  </div>
                  <div class="info-row">
                    <div class="info-label">Nom de la mère:</div>
                    <div class="info-value">${student.motherName || 'Non renseigné'}</div>
                  </div>
                  <div class="info-row">
                    <div class="info-label">Nom du tuteur:</div>
                    <div class="info-value">${student.guardianFullName || 'Non renseigné'}</div>
                  </div>
                  <div class="info-row">
                    <div class="info-label">Téléphone du tuteur:</div>
                    <div class="info-value">${student.guardianPhone || 'Non renseigné'}</div>
                  </div>
                </div>
              </div>

              ${studentEnrollments.length > 0 ? `
                <div class="info-section">
                  <h3>HISTORIQUE DES INSCRIPTIONS</h3>
                  <div style="overflow-x: auto;">
                    <table style="width: 100%; border-collapse: collapse;">
                      <thead>
                        <tr style="background-color: #f2f2f2;">
                          <th style="border: 1px solid #ddd; padding: 8px;">Année scolaire</th>
                          <th style="border: 1px solid #ddd; padding: 8px;">Classe</th>
                          <th style="border: 1px solid #ddd; padding: 8px;">Type</th>
                          <th style="border: 1px solid #ddd; padding: 8px;">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        ${studentEnrollments.map(enrollment => `
                          <tr>
                            <td style="border: 1px solid #ddd; padding: 8px;">${enrollment.schoolYear || 'N/A'}</td>
                            <td style="border: 1px solid #ddd; padding: 8px;">${enrollment.className}</td>
                            <td style="border: 1px solid #ddd; padding: 8px;">${enrollment.enrollmentType}</td>
                            <td style="border: 1px solid #ddd; padding: 8px;">${formatDate(enrollment.enrollmentDate)}</td>
                          </tr>
                        `).join('')}
                      </tbody>
                    </table>
                  </div>
                </div>
              ` : ''}

              <div class="footer">
                <p>Fiche générée le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}</p>
                <p>École ID: ${schoolId.substring(0, 8)}...</p>
              </div>
            </div>
          </body>
        </html>
      `;

      const printWindow = window.open('', '_blank');
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
      
      setMessage({ type: 'success', text: `Fiche technique de ${student.name} ouverte pour l'impression.` });
    } catch (e) {
      console.error("Error printing student profile:", e);
      setError("Erreur lors de la génération de la fiche technique: " + e.message);
    }
  }, [enrollments, schoolId, setError, setMessage]);

  // Fonction pour voir la fiche d'un élève
  const handleViewStudentProfile = useCallback((student) => {
    setCurrentStudent(student);
    setActiveTab('studentProfile');
  }, []);

  const renderContent = () => {
    if (isLoading && !studentsLoaded) {
      return (
        <div className="flex justify-center items-center h-48">
          <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
          <p className="ml-3 text-gray-700">Chargement des données...</p>
        </div>
      );
    }

    switch (activeTab) {
      case 'newStudent':
  return (
    <div className="bg-white p-6 md:p-8 rounded-2xl shadow-2xl border border-gray-100 transition duration-300">
      <h2 className="text-3xl font-extrabold text-blue-700 border-b-4 border-blue-500 pb-2 mb-6 inline-block">
        {isEditing ? 'Modifier Élève' : 'Enregistrer Nouvel Élève'}
      </h2>
      
      {/* Indicateur de version */}
      <div className="mb-4">
        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
          appVersion === 'v1' ? 'bg-gray-100 text-gray-800' :
          appVersion === 'v2' ? 'bg-blue-100 text-blue-800' :
          'bg-purple-100 text-purple-800'
        }`}>
          Version {appVersion.toUpperCase()}
          {appVersion === 'v1' && ' (Photo non disponible)'}
        </span>
      </div>
      
      <form onSubmit={isEditing ? handleUpdateStudent : handleCreateStudent} className="space-y-8">
        
        <h3 className="text-xl font-semibold text-gray-800 mb-4">Détails de l'Élève</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
          
          {/* Nom Complet */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700">Nom Complet <span className="text-red-500">*</span></label>
            <input
              type="text"
              id="name"
              value={newStudentName}
              onChange={(e) => setNewStudentName(e.target.value)}
              className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-150"
              required
            />
          </div>
          
          {/* Date de Naissance */}
          <div>
            <label htmlFor="dateOfBirth" className="block text-sm font-medium text-gray-700">Date de Naissance <span className="text-red-500">*</span></label>
            <input
              type="date"
              id="dateOfBirth"
              value={newStudentDateOfBirth}
              onChange={(e) => setNewStudentDateOfBirth(e.target.value)}
              className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-150"
              required
            />
          </div>
          
          {/* Lieu de Naissance */}
          <div>
            <label htmlFor="placeOfBirth" className="block text-sm font-medium text-gray-700">Lieu de Naissance</label>
            <input
              type="text"
              id="placeOfBirth"
              value={newStudentPlaceOfBirth}
              onChange={(e) => setNewStudentPlaceOfBirth(e.target.value)}
              className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-150"
            />
          </div>
          
          {/* Sexe */}
          <div>
            <label htmlFor="gender" className="block text-sm font-medium text-gray-700">Sexe <span className="text-red-500">*</span></label>
            <select
              id="gender"
              value={newStudentGender}
              onChange={(e) => setNewStudentGender(e.target.value)}
              className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-150 bg-white"
              required
            >
              <option value="">Sélectionner le sexe</option>
              <option value="Masculin">Masculin</option>
              <option value="Féminin">Féminin</option>
            </select>
          </div>
          
          {/* Adresse (Pleine largeur) */}
          <div className="md:col-span-2">
            <label htmlFor="address" className="block text-sm font-medium text-gray-700">Adresse</label>
            <input
              type="text"
              id="address"
              value={newStudentAddress}
              onChange={(e) => setNewStudentAddress(e.target.value)}
              className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-150"
            />
          </div>
        </div>
        
        {/* Section Photo - Conditionnée par la version */}
        {/* Section Photo - Conditionnée par la version */}
{isPhotoFeatureAvailable() && (
  <div className="pt-4 border-t-2 border-gray-100">
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-xl font-semibold text-gray-800">Photo de l'Élève</h3>
      <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full">
        V2/V3
      </span>
    </div>
    
    <div className="space-y-6">
      
      {/* Zone de drag & drop améliorée */}
      <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 hover:border-blue-400 transition-colors duration-200 bg-gray-50">
        
        {/* Aperçu de la photo existante ou nouvelle */}
        {(photoPreview || (isEditing && newStudentPhotoUrl)) ? (
          <div className="flex flex-col items-center">
            <p className="text-sm font-medium text-gray-700 mb-3">Aperçu :</p>
            <div className="relative w-40 h-40 border-4 border-white shadow-lg rounded-full overflow-hidden mb-4">
              <img 
                src={photoPreview || newStudentPhotoUrl} 
                alt="Aperçu" 
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = `
                    <div class="w-full h-full flex flex-col items-center justify-center bg-gray-100 text-gray-400">
                      <svg class="w-12 h-12 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                      </svg>
                      <span class="text-xs">Photo non chargée</span>
                    </div>
                  `;
                }}
              />
              
              {/* Badge de statut */}
              <div className="absolute bottom-2 right-2">
                {newStudentPhotoFile ? (
                  <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">Nouvelle</span>
                ) : isEditing && newStudentPhotoUrl ? (
                  <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">Existante</span>
                ) : null}
              </div>
            </div>
            
            {/* Bouton de changement */}
            <div className="flex gap-3">
              <label htmlFor="photo-upload" className="cursor-pointer">
                <div className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition duration-200 text-sm font-medium flex items-center">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"></path>
                  </svg>
                  Changer la photo
                </div>
                <input
                  type="file"
                  id="photo-upload"
                  className="hidden"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      if (file.size > 5 * 1024 * 1024) {
                        setError("La photo ne doit pas dépasser 5MB");
                        return;
                      }
                      setNewStudentPhotoFile(file);
                      const reader = new FileReader();
                      reader.onloadend = () => setPhotoPreview(reader.result);
                      reader.readAsDataURL(file);
                    }
                  }}
                />
              </label>
              
              {/* Bouton pour retirer la photo */}
              {(isEditing && newStudentPhotoUrl) && (
                <button
                  type="button"
                  onClick={() => {
                    setNewStudentPhotoUrl('');
                    setNewStudentPhotoFile(null);
                    setPhotoPreview(null);
                  }}
                  className="px-4 py-2 bg-red-50 text-red-700 border border-red-200 rounded-lg hover:bg-red-100 transition duration-200 text-sm font-medium flex items-center"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                  </svg>
                  Retirer
                </button>
              )}
            </div>
          </div>
        ) : (
          /* Zone de téléchargement quand aucune photo */
          <div className="text-center">
            <div className="mx-auto w-24 h-24 mb-4 flex items-center justify-center rounded-full bg-blue-50 text-blue-500">
              <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
              </svg>
            </div>
            
            <p className="text-gray-700 font-medium mb-2">Ajouter une photo de l'élève</p>
            <p className="text-sm text-gray-500 mb-4">Cela aidera à identifier rapidement l'élève</p>
            
            <label htmlFor="photo-upload-first" className="cursor-pointer">
              <div className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition duration-200 font-medium">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"></path>
                </svg>
                Télécharger une photo
              </div>
              <input
                type="file"
                id="photo-upload-first"
                className="hidden"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    if (file.size > 5 * 1024 * 1024) {
                      setError("La photo ne doit pas dépasser 5MB");
                      return;
                    }
                    setNewStudentPhotoFile(file);
                    const reader = new FileReader();
                    reader.onloadend = () => setPhotoPreview(reader.result);
                    reader.readAsDataURL(file);
                  }
                }}
              />
            </label>
            
            <p className="mt-3 text-xs text-gray-400">
              Formats acceptés: JPG, PNG, GIF • Taille max: 5MB
            </p>
          </div>
        )}
        
      </div>
      
      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <svg className="w-5 h-5 text-blue-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <div>
            <p className="text-sm font-medium text-blue-800 mb-1">Recommandations pour la photo</p>
            <ul className="text-xs text-blue-700 space-y-1">
              <li>• Photo récente et claire de l'élève</li>
              <li>• Fond neutre de préférence</li>
              <li>• Visage bien visible et éclairé</li>
              <li>• Format portrait recommandé</li>
            </ul>
          </div>
        </div>
      </div>
      
    </div>
  </div>
)}
              
              
        {/* Informations des Tuteurs */}
        <div className="pt-4 border-t-2 border-gray-100">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">Informations des Tuteurs / Parents</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
            
            {/* Nom Complet du Tuteur */}
            <div>
              <label htmlFor="guardianFullName" className="block text-sm font-medium text-gray-700">Nom Complet du Tuteur</label>
              <input
                type="text"
                id="guardianFullName"
                value={newStudentGuardianFullName}
                onChange={(e) => setNewStudentGuardianFullName(e.target.value)}
                className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-150"
              />
            </div>
          
            {/* Téléphone du Tuteur */}
            <div>
              <label htmlFor="guardianPhone" className="block text-sm font-medium text-gray-700">Téléphone du Tuteur</label>
              <input
                type="text"
                id="guardianPhone"
                value={newStudentGuardianPhone}
                onChange={(e) => setNewStudentGuardianPhone(e.target.value)}
                className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-150"
              />
            </div>
            
            {/* Nom du Père */}
            <div>
              <label htmlFor="fatherName" className="block text-sm font-medium text-gray-700">Nom du Père</label>
              <input
                type="text"
                id="fatherName"
                value={newStudentFatherName}
                onChange={(e) => setNewStudentFatherName(e.target.value)}
                className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-150"
              />
            </div>
            
            {/* Nom de la Mère */}
            <div>
              <label htmlFor="motherName" className="block text-sm font-medium text-gray-700">Nom de la Mère</label>
              <input
                type="text"
                id="motherName"
                value={newStudentMotherName}
                onChange={(e) => setNewStudentMotherName(e.target.value)}
                className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-150"
              />
            </div>
          </div>
        </div>

        {/* Boutons d'action */}
        <div className="pt-4 space-y-3">
          <button
            type="submit"
            className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-blue-700 transition duration-200 shadow-md hover:shadow-lg disabled:opacity-50"
            disabled={isLoading}
          >
            {isLoading ?
              'Traitement...' : (isEditing ? 'Mettre à jour l\'élève' : 'Enregistrer l\'élève')}
          </button>
          
          {isEditing && (
            <button
              type="button"
              onClick={() => {
                resetStudentForm();
                setActiveTab('studentList');
              }}
              className="w-full bg-gray-100 text-gray-700 py-3 px-4 rounded-lg font-medium hover:bg-gray-200 transition duration-200"
            >
              Annuler
            </button>
          )}
        </div>
      </form>
    </div>
  );

      case 'enrollment':
        return (
          <div className="bg-white p-6 md:p-8 rounded-2xl shadow-2xl border border-gray-100 transition duration-300">
            <h2 className="text-3xl font-extrabold text-blue-700 border-b-4 border-blue-500 pb-2 mb-6 inline-block">
              {isEditingEnrollment ? 'Modifier l\'Inscription' : 'Inscrire un Élève'}
            </h2>
            {canManageEnrollments ? (
              <form onSubmit={isEditingEnrollment ? handleUpdateEnrollment : handleCreateEnrollment} className="space-y-6">
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mode d'inscription
                  </label>
                  <div className="flex space-x-4">
                    <button
                      type="button"
                      onClick={() => setEnrollmentMode('single')}
                      className={`px-4 py-2 rounded-md font-medium transition duration-200 ${
                        enrollmentMode === 'single'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      Inscription unique
                    </button>
                    <button
                      type="button"
                      onClick={() => setEnrollmentMode('multiple')}
                      className={`px-4 py-2 rounded-md font-medium transition duration-200 ${
                        enrollmentMode === 'multiple'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      Inscription multiple
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                  
                  {enrollmentMode === 'single' && (
                    <div>
                      <label htmlFor="enrollmentStudent" className="block text-sm font-medium text-gray-700">
                        Sélectionner l'Élève <span className="text-red-500">*</span>
                      </label>
                      <select
                        id="enrollmentStudent"
                        value={newEnrollmentStudentId}
                        onChange={(e) => setNewEnrollmentStudentId(e.target.value)}
                        className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-150 bg-white"
                        required={enrollmentMode === 'single'}
                      >
                        <option value="">-- Choisir un élève --</option>
                        {students.map(student => (
                          <option key={student.id} value={student.id}>
                            {student.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  
                  {enrollmentMode === 'multiple' && (
                    <div className="md:col-span-2">
                      <label htmlFor="enrollmentStudents" className="block text-sm font-medium text-gray-700">
                        Sélectionner les Élèves <span className="text-red-500">*</span>
                      </label>
                      
                      <div className="mb-3">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                          <input
                            type="text"
                            placeholder="Rechercher un élève par nom..."
                            value={studentSearchTerm}
                            onChange={(e) => setStudentSearchTerm(e.target.value)}
                            className="pl-10 w-full border border-gray-300 rounded-lg shadow-sm p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-150"
                          />
                        </div>
                        <div className="mt-2 flex justify-between items-center">
                          <span className="text-sm text-gray-600">
                            {filteredStudentsForEnrollment.length} élève(s) trouvé(s)
                          </span>
                          <button
                            type="button"
                            onClick={handleSelectAllVisibleStudents}
                            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                          >
                            {(() => {
                              const visibleStudentIds = filteredStudentsForEnrollment.map(s => s.id);
                              const allSelected = visibleStudentIds.length > 0 && 
                                visibleStudentIds.every(id => newEnrollmentStudentIds.includes(id));
                              return allSelected ? 'Désélectionner tous les élèves visibles' : 'Sélectionner tous les élèves visibles';
                            })()}
                          </button>
                        </div>
                      </div>

                      <div className="mt-1 border border-gray-300 rounded-lg p-3 max-h-60 overflow-y-auto bg-gray-50">
                        {filteredStudentsForEnrollment.length > 0 ? (
                          filteredStudentsForEnrollment.map(student => (
                            <div key={student.id} className="flex items-center mb-2 p-2 hover:bg-gray-100 rounded">
                              <input
                                type="checkbox"
                                id={`student-${student.id}`}
                                checked={newEnrollmentStudentIds.includes(student.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setNewEnrollmentStudentIds([...newEnrollmentStudentIds, student.id]);
                                  } else {
                                    setNewEnrollmentStudentIds(newEnrollmentStudentIds.filter(id => id !== student.id));
                                  }
                                }}
                                className="h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                              />
                              <label htmlFor={`student-${student.id}`} className="ml-3 text-sm text-gray-700 flex-1">
                                <div className="font-medium">{student.name}</div>
                                <div className="text-xs text-gray-500">
                                  {student.gender} • {formatDate(student.dateOfBirth)}
                                </div>
                              </label>
                              <span className={`text-xs px-2 py-1 rounded-full ${
                                newEnrollmentStudentIds.includes(student.id)
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}>
                                {newEnrollmentStudentIds.includes(student.id) ? 'Sélectionné' : 'Non sélectionné'}
                              </span>
                            </div>
                          ))
                        ) : (
                          <div className="text-center py-4 text-gray-500">
                            {studentSearchTerm ? 'Aucun élève trouvé pour cette recherche.' : 'Aucun élève disponible.'}
                          </div>
                        )}
                      </div>
                      
                      <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                        <div className="flex justify-between items-center">
                          <div>
                            <span className="text-sm font-medium text-blue-800">
                              {newEnrollmentStudentIds.length} élève(s) sélectionné(s)
                            </span>
                            <p className="text-xs text-blue-600 mt-1">
                              {studentSearchTerm && '⚠️ Seuls les élèves correspondant à la recherche sont affichés.'}
                            </p>
                          </div>
                          <div className="space-x-2">
                            <button
                              type="button"
                              onClick={() => setNewEnrollmentStudentIds([])}
                              className="text-sm bg-red-100 text-red-700 hover:bg-red-200 px-3 py-1 rounded-md transition duration-150"
                            >
                              Tout désélectionner
                            </button>
                            <button
                              type="button"
                              onClick={() => setNewEnrollmentStudentIds(students.map(s => s.id))}
                              className="text-sm bg-green-100 text-green-700 hover:bg-green-200 px-3 py-1 rounded-md transition duration-150"
                            >
                              Sélectionner tous les élèves
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div>
                    <label htmlFor="enrollmentClass" className="block text-sm font-medium text-gray-700">
                      Sélectionner la Classe <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="enrollmentClass"
                      value={newEnrollmentClassId}
                      onChange={(e) => {
                        const selectedId = e.target.value;
                        setNewEnrollmentClassId(selectedId);
                        const selectedClass = classes.find(cls => cls.id === selectedId);
                        setNewEnrollmentClassName(selectedClass ? selectedClass.nom : '');
                      }}
                      className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-150 bg-white"
                      required
                    >
                      <option value="">-- Choisir une classe --</option>
                      {classes.map(cls => (
                        <option key={cls.id} value={cls.id}>
                          {cls.nom}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label htmlFor="schoolYear" className="block text-sm font-medium text-gray-700">
                      Année Scolaire <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="schoolYear"
                      value={newEnrollmentSchoolYear}
                      onChange={(e) => setNewEnrollmentSchoolYear(e.target.value)}
                      className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-150 bg-white"
                      required
                    >
                      {schoolYearOptions.map((year) => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="enrollmentDate" className="block text-sm font-medium text-gray-700">
                      Date d'Inscription <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      id="enrollmentDate"
                      value={newEnrollmentDate}
                      onChange={(e) => setNewEnrollmentDate(e.target.value)}
                      className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-150"
                      required
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label htmlFor="enrollmentType" className="block text-sm font-medium text-gray-700">
                      Type d'Inscription <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="enrollmentType"
                      value={newEnrollmentType}
                      onChange={(e) => setNewEnrollmentType(e.target.value)}
                      className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-150 bg-white"
                      required
                    >
                      <option value="normal">Normal</option>
                      <option value="boursier">Boursier</option>
                      <option value="demi-boursier">Demi Boursier</option>
                      <option value="social">Social</option>
                    </select>
                  </div>
                </div>
                
                {(newEnrollmentType === 'boursier' || newEnrollmentType === 'demi-boursier' || newEnrollmentType === 'social') && (
                  <div className="md:col-span-2 pt-4">
                    <label htmlFor="borsierResponsibleName" className="block text-sm font-medium text-gray-700">
                      Nom du Responsable (Bourse/Social)
                    </label>
                    <input
                      type="text"
                      id="borsierResponsibleName"
                      value={newEnrollmentBorsierResponsibleName}
                      onChange={(e) => setNewEnrollmentBorsierResponsibleName(e.target.value)}
                      className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-150"
                      required={newEnrollmentType !== 'social'}
                    />
                  </div>
                )}
                
                <div className="pt-4 border-t-2 border-gray-100 space-y-4">
                  <div className="flex items-center">
                    <input
                      id="isCasSocial"
                      name="isCasSocial"
                      type="checkbox"
                      checked={isCasSocial}
                      onChange={(e) => setIsCasSocial(e.target.checked)}
                      className="h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="isCasSocial" className="ml-3 block text-base font-medium text-gray-700">
                      Activer la tarification **Cas Social**
                    </label>
                  </div>

                  {isCasSocial && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 border p-6 rounded-xl bg-blue-50/50 shadow-inner">
                      <h4 className="md:col-span-2 text-lg font-semibold text-blue-800">
                        Paramètres de Tarification Spéciale
                        {enrollmentMode === 'multiple' && (
                          <span className="text-sm font-normal text-blue-600 ml-2">
                            (Appliquer à tous les élèves sélectionnés)
                          </span>
                        )}
                      </h4>

                      <div>
                        <label htmlFor="casSocialFeeType" className="block text-sm font-medium text-gray-700">
                          Type de Tarification
                        </label>
                        <select
                          id="casSocialFeeType"
                          value={casSocialFeeType}
                          onChange={(e) => setCasSocialFeeType(e.target.value)}
                          className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm p-3 bg-white"
                        >
                          <option value="annual">Montant annuel fixe</option>
                          <option value="percentage">Pourcentage de réduction (%)</option>
                        </select>
                      </div>

                      <div>
                        <label htmlFor="casSocialFeeValue" className="block text-sm font-medium text-gray-700">
                          {casSocialFeeType === 'annual' ? "Montant annuel à payer (XOF)" : "Pourcentage de réduction (%)"}
                        </label>
                        <input
                          id="casSocialFeeValue"
                          type="number"
                          min="1"
                          max={casSocialFeeType === 'percentage' ? "100" : undefined}
                          value={casSocialFeeValue}
                          onChange={(e) => setCasSocialFeeValue(e.target.value)}
                          className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm p-3"
                          placeholder={casSocialFeeType === 'annual' ? "Ex: 150000" : "Ex: 50 (pour 50%)"}
                          required
                        />
                        <p className="mt-1 text-xs text-gray-600">
                          {casSocialFeeType === 'annual'
                            ? "Montant total annuel que l'élève doit payer."
                            : "Pourcentage de réduction applicable sur les frais standard."
                          }
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="pt-4 space-y-3">
                  <button
                    type="submit"
                    className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-blue-700 transition duration-200 shadow-md hover:shadow-lg disabled:opacity-50"
                    disabled={isLoading || isEnrolling}
                  >
                    {isEnrolling
                      ? 'Traitement...'
                      : enrollmentMode === 'single'
                        ? (isEditingEnrollment ? 'Mettre à jour l\'inscription' : 'Enregistrer l\'inscription')
                        : `Inscrire ${newEnrollmentStudentIds.length} élève(s)`
                    }
                  </button>
                  
                  {isEditingEnrollment && (
                    <button
                      type="button"
                      onClick={resetEnrollmentForm}
                      className="w-full bg-gray-100 text-gray-700 py-3 px-4 rounded-lg font-medium hover:bg-gray-200 transition duration-200"
                    >
                      Annuler
                    </button>
                  )}
                </div>
              </form>
            ) : (
              <p className="text-red-600 bg-red-50 p-4 rounded-lg border border-red-200">
                ⚠️ Vous n'avez pas les autorisations nécessaires pour gérer les inscriptions.
              </p>
            )}
          </div>
        );

      case 'studentProfile':
        return (
          <div className="p-6 bg-white rounded-2xl shadow-xl border border-gray-100">
            <h2 className="text-3xl font-extrabold text-blue-700 border-b-4 border-blue-500 pb-2 mb-6 inline-block">
              Fiche Technique de l'Élève
            </h2>
            
            <button
              onClick={() => setActiveTab('studentList')}
              className="mb-6 text-blue-600 hover:text-blue-800 font-medium flex items-center"
            >
              <span className="mr-2">←</span> Retour à la liste
            </button>
            
            {currentStudent ? (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-1">
  <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
    {/* Photo de l'élève - Conditionnée par la version */}
    {/* Photo de l'élève - Conditionnée par la version */}
{isPhotoFeatureAvailable() ? (
  <div className="mb-6">
    <div className="flex items-center justify-between mb-3">
      <h3 className="text-lg font-semibold text-gray-800">Photo</h3>
      {currentStudent.photoUrl && (
        <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full">
          Photo disponible
        </span>
      )}
    </div>
    <div className="relative w-48 h-48 mx-auto border-4 border-white shadow-xl rounded-full overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100">
      {currentStudent.photoUrl ? (
        <>
          <img
            src={currentStudent.photoUrl}
            alt={`Photo de ${currentStudent.name}`}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 24 24'%3E%3Cpath fill='%23999' d='M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z'/%3E%3C/svg%3E";
            }}
          />
          {/* Overlay au survol */}
          <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 transition duration-200 flex items-center justify-center opacity-0 hover:opacity-100">
            <span className="text-white text-xs font-medium bg-black bg-opacity-50 px-2 py-1 rounded">
              Photo profil
            </span>
          </div>
        </>
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center text-gray-400">
          <svg className="w-16 h-16 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
          </svg>
          <span className="text-sm">Aucune photo</span>
          <span className="text-xs mt-1">Version {appVersion}</span>
        </div>
      )}
    </div>
  </div>
) : (
  <div className="mb-6 p-4 bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-xl">
    <div className="flex items-center">
      <svg className="w-5 h-5 text-yellow-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.998-.833-2.732 0L4.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
      </svg>
      <div>
        <p className="text-sm font-medium text-yellow-800">Fonctionnalité photo</p>
        <p className="text-xs text-yellow-700 mt-1">
          Disponible uniquement en version V2/V3. Vous utilisez actuellement la version {appVersion.toUpperCase()}.
        </p>
      </div>
    </div>
  </div>
)}


                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-gray-800">Informations principales</h3>
                      <div className="space-y-3">
                        <div>
                          <span className="text-sm font-medium text-gray-600">Nom complet:</span>
                          <p className="font-semibold text-gray-900 text-lg">{currentStudent.name}</p>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-600">Date de naissance:</span>
                          <p className="font-medium text-gray-900">{formatDate(currentStudent.dateOfBirth)}</p>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-600">Lieu de naissance:</span>
                          <p className="font-medium text-gray-900">{currentStudent.placeOfBirth || 'Non renseigné'}</p>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-600">Sexe:</span>
                          <p className="font-medium text-gray-900">{currentStudent.gender}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="lg:col-span-2">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
                      <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                        <User className="mr-2" size={20} /> Informations personnelles
                      </h3>
                      <div className="space-y-3">
                        <div>
                          <span className="text-sm font-medium text-gray-600">Adresse:</span>
                          <p className="font-medium text-gray-900">{currentStudent.address || 'Non renseignée'}</p>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-600">Date d'enregistrement:</span>
                          <p className="font-medium text-gray-900">{formatDate(currentStudent.createdAt)}</p>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-600">Dernière mise à jour:</span>
                          <p className="font-medium text-gray-900">{formatDate(currentStudent.updatedAt)}</p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
                      <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                        <Users className="mr-2" size={20} /> Informations familiales
                      </h3>
                      <div className="space-y-3">
                        <div>
                          <span className="text-sm font-medium text-gray-600">Nom du père:</span>
                          <p className="font-medium text-gray-900">{currentStudent.fatherName || 'Non renseigné'}</p>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-600">Nom de la mère:</span>
                          <p className="font-medium text-gray-900">{currentStudent.motherName || 'Non renseigné'}</p>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-600">Nom du tuteur:</span>
                          <p className="font-medium text-gray-900">{currentStudent.guardianFullName || 'Non renseigné'}</p>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-600">Téléphone tuteur:</span>
                          <p className="font-medium text-gray-900">{currentStudent.guardianPhone || 'Non renseigné'}</p>
                        </div>
                      </div>
                    </div>

                    <div className="md:col-span-2 bg-gray-50 p-6 rounded-xl border border-gray-200">
                      <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                        <ClipboardCheck className="mr-2" size={20} /> Inscriptions actuelles
                      </h3>
                      {enrollments.filter(e => e.studentId === currentStudent.id).length > 0 ? (
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-100">
                              <tr>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Année scolaire</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Classe</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Type</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Date inscription</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                              {enrollments
                                .filter(e => e.studentId === currentStudent.id)
                                .sort((a, b) => new Date(b.enrollmentDate) - new Date(a.enrollmentDate))
                                .map(enrollment => (
                                  <tr key={enrollment.id} className="hover:bg-gray-50">
                                    <td className="px-4 py-2 text-sm text-gray-700">{enrollment.schoolYear}</td>
                                    <td className="px-4 py-2 text-sm text-gray-700">{enrollment.className}</td>
                                    <td className="px-4 py-2 text-sm">
                                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                        enrollment.enrollmentType === 'normal' ? 'bg-green-100 text-green-800' :
                                        enrollment.enrollmentType === 'boursier' ? 'bg-blue-100 text-blue-800' :
                                        enrollment.enrollmentType === 'demi-boursier' ? 'bg-yellow-100 text-yellow-800' :
                                        'bg-purple-100 text-purple-800'
                                      }`}>
                                        {enrollment.enrollmentType}
                                      </span>
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-700">
                                      {formatDate(enrollment.enrollmentDate)}
                                    </td>
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className="text-gray-500 italic">Aucune inscription trouvée pour cet élève.</p>
                      )}
                    </div>
                  </div>

                  <div className="mt-8 flex flex-wrap gap-3">
                    <button
                      onClick={() => handleEditStudent(currentStudent)}
                      className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition duration-200 flex items-center"
                    >
                      <Edit size={18} className="mr-2" /> Modifier cet élève
                    </button>
                    <button
                      onClick={() => handlePrintStudentProfile(currentStudent)}
                      className="bg-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-700 transition duration-200 flex items-center"
                    >
                      <Printer size={18} className="mr-2" /> Imprimer la fiche
                    </button>
                    <button
                      onClick={() => {
                        setActiveTab('enrollment');
                        setNewEnrollmentStudentId(currentStudent.id);
                      }}
                      className="bg-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-700 transition duration-200 flex items-center"
                    >
                      <UserPlus size={18} className="mr-2" /> Inscrire cet élève
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <User size={64} className="mx-auto text-gray-300 mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">Aucun élève sélectionné</h3>
                <p className="text-gray-600">Veuillez sélectionner un élève depuis la liste pour voir sa fiche technique.</p>
                <button
                  onClick={() => setActiveTab('studentList')}
                  className="mt-4 bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700"
                >
                  Voir la liste des élèves
                </button>
              </div>
            )}
          </div>
        );

      case 'enrolledList':
        return (
          <div className="p-4 bg-white rounded-lg shadow-md">
            <h2 className="text-2xl font-semibold mb-4 text-gray-800">Liste des Inscriptions</h2>
            
            <div className="mb-4 grid grid-cols-1 md:grid-cols-3 gap-4">
              <input
                type="text"
                placeholder="Rechercher élève par nom..."
                value={enrollmentSearchTerm}
                onChange={(e) => setEnrollmentSearchTerm(e.target.value)}
                className="block w-full border border-gray-300 rounded-md shadow-sm p-2"
              />

              <select
                value={enrollmentFilterSchoolYear}
                onChange={(e) => setEnrollmentFilterSchoolYear(e.target.value)}
                className="block w-full border border-gray-300 rounded-md shadow-sm p-2"
              >
                <option value="">Toutes les années scolaires</option>
                {schoolYearOptions.map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>

              <select
                value={enrollmentFilterClassId}
                onChange={(e) => setEnrollmentFilterClassId(e.target.value)}
                className="block w-full border border-gray-300 rounded-md shadow-sm p-2"
              >
                <option value="">Toutes les classes</option>
                {classes.map(cls => (
                  <option key={cls.id} value={cls.id}>{cls.nom}</option>
                ))}
              </select>
            </div>

            {filteredEnrollments.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Élève</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date de Naissance</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Classe</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Année Scolaire</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type d'Inscription</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tarif Social</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date d'Inscription</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredEnrollments.map((enrollment) => {
                      const student = students.find(s => s.id === enrollment.studentId);
                      return (
                        <tr key={enrollment.id} className="bg-white">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{enrollment.studentName}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {student?.dateOfBirth ? formatDate(student.dateOfBirth) : 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{enrollment.className}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{enrollment.schoolYear || 'N/A'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{enrollment.enrollmentType || 'N/A'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {enrollment.isCasSocial ?
                              (enrollment.casSocialFeeType === 'annual' ? `${enrollment.casSocialFeeValue} XOF` : `${enrollment.casSocialFeeValue}%`)
                              : 'Non'
                            }
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(enrollment.enrollmentDate)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <button
                              onClick={() => {
                                const student = students.find(s => s.id === enrollment.studentId);
                                if (student) handleViewStudentProfile(student);
                              }}
                              className="text-blue-600 hover:text-blue-900 p-2 rounded-full hover:bg-blue-100 flex items-center"
                              title="Voir la fiche de l'élève"
                            >
                              <FileText size={18} className="mr-2" /> Fiche
                            </button>
                            {canManageEnrollments && (
                              <button
                                onClick={() => handleEditEnrollment(enrollment)}
                                className="text-indigo-600 hover:text-indigo-900 p-2 rounded-full hover:bg-indigo-100 flex items-center mt-2"
                                title="Modifier cette inscription"
                              >
                                <Edit size={18} className="mr-2" /> Éditer
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-gray-600 italic">
                {canManageEnrollments ?
                  "Aucune inscription trouvée pour les critères sélectionnés." : ""}
              </p>
            )}
          </div>
        );

      case 'studentList':
  return (
    <div className="p-4 bg-white rounded-lg shadow-md">
      <h2 className="text-2xl font-semibold mb-4 text-gray-800">Liste des Élèves</h2>
      
      {/* Indicateur de version */}
      {appVersion === 'v1' && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            ℹ️ Version V1 - La fonctionnalité photo des élèves est disponible en V2/V3
          </p>
        </div>
      )}
      
      <div className="mb-4">
        <input
          type="text"
          placeholder="Rechercher par nom..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>
            
            {filteredStudents.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nom</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date de Naissance</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sexe</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredStudents.map(student => (
                      <tr key={student.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{student.name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(student.dateOfBirth)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{student.gender}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          {canManageStudents && (
                            <>
                              <button
                                onClick={() => handleViewStudentProfile(student)}
                                className="text-blue-600 hover:text-blue-900 mr-4"
                                title="Voir la fiche technique"
                              >
                                Voir fiche
                              </button>
                              <button
                                onClick={() => handleEditStudent(student)}
                                className="text-indigo-600 hover:text-indigo-900 mr-4"
                                title="Modifier cet élève"
                              >
                                Éditer
                              </button>
                              <button
                                onClick={() => handleDeleteStudent(student.id)}
                                className="text-red-600 hover:text-red-900"
                                title="Supprimer cet élève"
                              >
                                Supprimer
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-gray-600 italic">Aucun élève trouvé.</p>
            )}
          </div>
        );
        
      default:
        return null;
    }
  };

  return (
    <div className="container mx-auto p-4">
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Gestion des Élèves et Inscriptions</h1>
          <p className="text-gray-600">Gérez les informations des élèves et leurs inscriptions au sein de votre école.</p>
        </div>

        <nav className="bg-gray-100 p-4 border-b border-gray-200">
          <ul className="flex flex-wrap gap-2">
            {canManageStudents && (
              <li>
                <button
                  onClick={() => { setActiveTab('newStudent'); setIsEditing(false); }}
                  className={`py-2 px-4 rounded-md text-sm font-medium transition duration-200 ${activeTab === 'newStudent' ? 'bg-blue-600 text-white shadow' : 'text-gray-700 hover:bg-gray-200'}`}
                >
                  <UserPlus className="inline-block mr-2" size={18} /> Enregistrer Élève
                </button>
              </li>
            )}
            {canManageEnrollments && (
              <li>
                <button
                  onClick={() => { setActiveTab('enrollment'); resetEnrollmentForm(); }}
                  className={`py-2 px-4 rounded-md text-sm font-medium transition duration-200 ${activeTab === 'enrollment' ? 'bg-blue-600 text-white shadow' : 'text-gray-700 hover:bg-gray-200'}`}
                >
                  <ClipboardList className="inline-block mr-2" size={18} /> Inscription
                </button>
              </li>
            )}
            <li>
              <button
                onClick={() => setActiveTab('studentList')}
                className={`py-2 px-4 rounded-md text-sm font-medium transition duration-200 ${activeTab === 'studentList' ? 'bg-blue-600 text-white shadow' : 'text-gray-700 hover:bg-gray-200'}`}
              >
                <User className="inline-block mr-2" size={18} /> Liste des Élèves
              </button>
            </li>
            <li>
              <button
                onClick={() => setActiveTab('studentProfile')}
                className={`py-2 px-4 rounded-md text-sm font-medium transition duration-200 ${activeTab === 'studentProfile' ? 'bg-blue-600 text-white shadow' : 'text-gray-700 hover:bg-gray-200'}`}
              >
                <FileText className="inline-block mr-2" size={18} /> Fiche Technique
              </button>
            </li>
            <li>
              <button
                onClick={() => setActiveTab('enrolledList')}
                className={`py-2 px-4 rounded-md text-sm font-medium transition duration-200 ${activeTab === 'enrolledList' ? 'bg-blue-600 text-white shadow' : 'text-gray-700 hover:bg-gray-200'}`}
              >
                <ClipboardCheck className="inline-block mr-2" size={18} /> Liste des Inscriptions
              </button>
            </li>
            {canAccessExcelImport && onSwitchToExcelImport && (
              <li>
                <button
                  onClick={onSwitchToExcelImport}
                  className="py-2 px-4 rounded-md text-sm font-medium transition duration-200 bg-green-600 text-white hover:bg-green-700"
                >
                  <FileUp className="inline-block mr-2" size={18} /> Import Excel
                </button>
              </li>
            )}
          </ul>
        </nav>

        <div className="p-4">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default ElevesManagement;